<template>
    <div v-if="details">

        <v-row>
            <v-col class="d-flex" cols="12" sm="5">
                <v-card class="pa-2" tile flat>
                    <div class="d-flex align-center flex-grow-1 flex-shrink-1 ma-0 pa-0 body-2 mb-4 secondary--text text-truncate">
                        <back-button/>
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.back') }}
                    </div>

                    <div class="headline font-weight-bold mb-5 mt-1" v-if="purchaseId">
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.order_title_long', purchaseId,
                        $options.filters.date(purchaseCreatedAt)) }}
                    </div>

                    <div class="position-relative">
                        <v-img :src="productImageSrc(details.product)"
                               class="flex-grow-0 mb-2"
                               aspect-ratio="1"></v-img>
                        <div class="badge body-1 font-weight-bold" :class="`badge--${details.status}`">
                            {{ $vuetify.lang.t(`$vuetify.widgets.orders.${details.status}`) }}
                        </div>
                    </div>

                    <div class="py-0 body-1 text-uppercase">
                        {{ details.product.product_brand }}
                    </div>
                    <div class="pt-0 font-weight-bold headline">
                        {{ details.product.title }}
                    </div>

                    <div class="py-0 body-1 pt-2">
                        {{ details.quantity }}x{{ details.price | number }} {{ $vuetify.lang.t('$vuetify.points.long') }}
                    </div>
                    <div class="pt-0 font-weight-bold headline">
                        {{ details.quantity * details.price | number }} {{ $vuetify.lang.t('$vuetify.points.long') }}
                    </div>

                    <div class="pt-0 body-1 font-weight-bold pt-2">
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.order') }}:
                    </div>
                    <div class="body-1">
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.order_title_short', purchaseId,
                        $options.filters.date(purchaseCreatedAt)) }}
                    </div>

                    <div class="pt-0 body-1 font-weight-bold pt-2">
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.address') }}:
                    </div>
                    <div class="body-1" v-if="shipping">
                        {{ shipping.delivery_address.delivery_address_firstname }}
                        {{ shipping.delivery_address.delivery_address_lastname }}<br>
                        <template v-if="shipping.delivery_address.delivery_address_company">
                            {{ shipping.delivery_address.delivery_address_company }}<br>
                        </template>
                        {{ shipping.delivery_address.delivery_address_street }}
                        {{ shipping.delivery_address.delivery_address_number }},
                        {{ shipping.delivery_address.delivery_address_zip }}
                        {{ shipping.delivery_address.delivery_address_city }}
                    </div>

                </v-card>
            </v-col>
            <v-col class="d-flex" cols="12" sm="7">
                <v-card class="pa-2" tile flat>
                    <error-list :errors="errors"></error-list>
                    <build-form :title="$vuetify.lang.t('$vuetify.widgets.orders.message')"
                                :form-items="items"
                                :actions="actions"
                                :rules="rules"
                                item-text="reason"
                                item-value="reason"
                                :form.sync="form"
                                :loader-key="loader"
                                ref="form"
                                class="px-2"/>
                </v-card>
            </v-col>
        </v-row>
    </div>
    <!-- page error -->
    <v-container v-else>
        <page-not-found/>
    </v-container>
    <!-- /page error -->
</template>
<script>
    // 3rd
    import {
        mapActions,
        mapState
    } from 'vuex';
    // components
    import BackButton from '../../components/BackButton';
    import PageNotFound from '../../components/PageNotFound';
    import BuildForm from '../../components/Builders/BuildForm';
    import ErrorList from '../../components/ErrorList';
    // models
    import Order from '../../models/Order';
    import CompletedOrder from '../../models/CompletedOrder';
    // utils
    import {
        redirect,
        setPageTitle,
    } from '../../utils/helper';
    import {
        message,
        reason,
    } from '../../utils/formRules';
    import { errorHandler } from '../../utils/api';
    import {
        LOADER_SUBMIT_FEEDBACK,
        ROUTE_CONFIRMATION,
        ROUTE_MY_ORDERS,
    } from '../../utils/constants';

    export default {
        name: 'order-item-detail',
        props: ['orderItemId'],
        components: {
            BackButton,
            PageNotFound,
            BuildForm,
            ErrorList,
        },
        data() {
            return {
                errors: {},
                form: {
                    order_item_id: null,
                    reason: '',
                    message: '',
                },
                rules: {
                    reason,
                    message,
                },
            }
        },
        methods: {
            productImageSrc(product) {
                return product.images.length > 0 ? product.images[0].source : '//via.placeholder.com/150x150/a1a1a1/FF0000/?text=Fallback';
            },
            ...mapActions('confirmation', [
                'writeConfirmation',
            ]),
            async submit() {
                this.form.order_item_id = this.orderItemId;

                let result = null;
                try {
                    result = await Order.submitFeedbackForm(this.form);
                } catch (e) {
                    this.errors = errorHandler(e);
                    return;
                }

                const { data: { message } } = result;
                this.writeConfirmation({
                    headline: '$vuetify.confirmation.send_feedback',
                    description: message,
                    button: '$vuetify.buttons.back_to_my_orders',
                    buttonUrl: { name: ROUTE_MY_ORDERS },
                });
                redirect(ROUTE_CONFIRMATION);

                this.form.reason = '';
                this.form.message = '';
            },
        },
        computed: {
            ...mapState([
                'orderReasons',
            ]),
            loader() {
                return LOADER_SUBMIT_FEEDBACK;
            },
            items() {
                return [
                    {
                        is: 'select',
                        name: 'reason',
                        options: this.reasons,
                    },
                    {
                        is: 'textarea',
                        name: 'message',
                    },
                    /*
                    {
                        is: 'hint',
                        content: 'feedback',
                    },
                    */
                ]
            },
            actions() {
                return [
                    {
                        is: 'button',
                        name: 'submit_contact',
                        color: 'primary',
                        clickMethod: this.submit,
                    },
                ]
            },
            reasons() {
                return this.orderReasons;
            },
            shipping() {
                return Order.getShippingInfo(this.orderItemId) || CompletedOrder.getShippingInfo(this.orderItemId);
            },
            details() {
                return Order.getDetails(this.orderItemId) || CompletedOrder.getDetails(this.orderItemId);
            },
            purchaseId() {
                return this.details.transaction ? this.details.transaction.meta.purchase_id : this.details.order.order_number;
            },
            purchaseCreatedAt() {
                return this.details.transaction ? this.details.transaction.created_at : this.details.order.created_at;
            },
        }
    }
</script>
